import apiClient, { safeJson } from '@/api/client';
import {
    AddTeamMemberBody,
    BackfillResult,
    CenterCoachListResponse,
    CenterSalesManagersResponse,
    CreateTeamBody,
    CreateTeamResponse,
    GetTeamResponse,
    ListTeamsResponse,
    SuperAdminTeamBreakdownResponse,
    SuperAdminTeamsResponse,
    Team,
    TeamMemberInput,
    TeamMembership,
} from './types';

// === 팀 기능 노출 여부 (슈퍼관리자 산하 센터만) ===

export const getTeamFeatureStatusAPI = async (centerId: string) => {
    const res = await apiClient.get(`v1/centers/${centerId}/team-feature-status`);
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as { enabled: boolean; brandName: string | null } | null,
    };
};

// === 강사 옵션 (선택용) ===

export const getCenterCoachesAPI = async (centerId: string) => {
    const res = await apiClient.get(`v1/centers/coach-list/${centerId}`, {
        searchParams: { sort: 'alphabetasc' },
    });
    const data = await safeJson(res);
    return { status: res.status, data: data as CenterCoachListResponse | null };
};

// === 판매담당자 옵션 (강사 + 영업담당) ===

export const getCenterSalesManagersAPI = async (centerId: string) => {
    const res = await apiClient.get(`v1/membershippayments/salesManagers/${centerId}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as CenterSalesManagersResponse | null };
};

// === 센터관리자 — 팀 CRUD ===

export const createTeamAPI = async (centerId: string, body: CreateTeamBody) => {
    const res = await apiClient.post(`v1/centers/${centerId}/teams`, { json: body });
    const data = await safeJson(res);
    return { status: res.status, data: data as CreateTeamResponse | null };
};

export const listTeamsAPI = async (centerId: string, status: 'active' | 'all' = 'active') => {
    const res = await apiClient.get(`v1/centers/${centerId}/teams`, {
        searchParams: { status },
    });
    const data = await safeJson(res);
    return { status: res.status, data: data as ListTeamsResponse | null };
};

export const getTeamAPI = async (centerId: string, teamId: string) => {
    const res = await apiClient.get(`v1/centers/${centerId}/teams/${teamId}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as GetTeamResponse | null };
};

export const updateTeamAPI = async (
    centerId: string,
    teamId: string,
    body: { name?: string; description?: string },
) => {
    const res = await apiClient.patch(`v1/centers/${centerId}/teams/${teamId}`, {
        json: body,
    });
    const data = await safeJson(res);
    return { status: res.status, data: data as { team: Team } | null };
};

export const deleteTeamAPI = async (centerId: string, teamId: string) => {
    const res = await apiClient.delete(`v1/centers/${centerId}/teams/${teamId}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as { team: Team } | null };
};

// === 팀원 ===

export const addTeamMemberAPI = async (
    centerId: string,
    teamId: string,
    body: AddTeamMemberBody,
) => {
    const res = await apiClient.post(`v1/centers/${centerId}/teams/${teamId}/members`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as { membership: TeamMembership; transferredFrom: string | null } | null,
    };
};

export const removeTeamMemberAPI = async (centerId: string, teamId: string, userId: string) => {
    const res = await apiClient.delete(
        `v1/centers/${centerId}/teams/${teamId}/members/${userId}`,
    );
    const data = await safeJson(res);
    return { status: res.status, data: data as { membership: TeamMembership } | null };
};

export const rebackfillTeamAPI = async (
    centerId: string,
    teamId: string,
    body: { effectiveFrom?: string | null },
) => {
    const res = await apiClient.post(
        `v1/centers/${centerId}/teams/${teamId}/rebackfill`,
        { json: body },
    );
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as {
            ok: boolean;
            effectiveFrom: string;
            backfillResult: BackfillResult;
        } | null,
    };
};

export const changeTeamLeaderAPI = async (
    centerId: string,
    teamId: string,
    body: TeamMemberInput,
) => {
    const res = await apiClient.patch(`v1/centers/${centerId}/teams/${teamId}/leader`, {
        json: body,
    });
    const data = await safeJson(res);
    return { status: res.status, data: data as { team: Team } | null };
};

// === 슈퍼관리자 분석 ===

export const getSuperAdminTeamsAPI = async (
    superAdminId: string,
    params: {
        from: string;
        to: string;
        centerIds?: string[];
        includeDeleted?: boolean;
        includeGroup?: boolean;
    },
) => {
    const search: Record<string, string> = { from: params.from, to: params.to };
    if (params.centerIds && params.centerIds.length > 0) {
        // ky의 searchParams가 string만 받으니 직접 직렬화
        params.centerIds.forEach((id, idx) => {
            search[`centerIds[${idx}]`] = id;
        });
    }
    if (params.includeDeleted !== undefined) search.includeDeleted = String(params.includeDeleted);
    if (params.includeGroup !== undefined) search.includeGroup = String(params.includeGroup);

    const res = await apiClient.get(`v1/superadmins/dashboard/teams/${superAdminId}`, {
        searchParams: search,
    });
    const data = await safeJson(res);
    return { status: res.status, data: data as SuperAdminTeamsResponse | null };
};

export const getSuperAdminTeamBreakdownAPI = async (
    superAdminId: string,
    teamId: string,
    params: { from: string; to: string; includeGroup?: boolean },
) => {
    const search: Record<string, string> = { from: params.from, to: params.to };
    if (params.includeGroup !== undefined) search.includeGroup = String(params.includeGroup);
    const res = await apiClient.get(
        `v1/superadmins/dashboard/teams/${superAdminId}/${teamId}/breakdown`,
        { searchParams: search },
    );
    const data = await safeJson(res);
    return { status: res.status, data: data as SuperAdminTeamBreakdownResponse | null };
};
